import {
  Box,
  Checkbox,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from "@mui/material";
import { useTiers } from "api/tiers";
import { Search } from "components/FormControls/Select/Search";
import { OLTabs } from "components/Tabs/OLTabs";
import { Field, FieldProps } from "formik";
import { useMessage } from "hooks/intl";
import { useDebounce } from "hooks/useDebounce";
import { useEffect, useMemo, useRef, useState } from "react";
import { InputSize } from "../styles";
import { useStyles } from "./styles";
import { Tier, Value } from "./Value";

interface Props {
  "data-test-id"?: string;
  className?: string;
  disabled?: boolean;
  label?: React.ReactNode;
  multiple?: boolean;
  name: string;
  placeholder?: string;
  size?: InputSize;
  onChange?: (value: any) => void;
}

export const TierSetDropdown = ({
  "data-test-id": dataTestId,
  className,
  disabled,
  label,
  multiple = true,
  name,
  placeholder,
  size = "medium",
  onChange,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchedValue, setSearchedValue] = useState("");
  const [activeTab, setActiveTab] = useState<string>();
  const { debouncedValue: searchQuery, isTyping } = useDebounce(searchedValue);
  const { message } = useMessage();
  const classes = useStyles({ size });
  const anchorRef = useRef<HTMLInputElement | HTMLDivElement>(null);
  const { useListOfTierSets } = useTiers();
  const { data: tierSetList } = useListOfTierSets();
  const filteredTierSetList = useMemo(
    () =>
      searchQuery
        ? tierSetList?.items
            .map((tierset) => ({
              ...tierset,
              tiers: tierset.tiers?.filter((tier) =>
                tier.name.toLowerCase().includes(searchQuery.toLowerCase()),
              ),
            }))
            .filter((tierset) => tierset.tiers?.length > 0)
        : tierSetList?.items,
    [searchQuery, tierSetList?.items],
  );
  const tabs =
    filteredTierSetList?.map((tierset) => {
      return {
        id: tierset.tierSetId,
        name: tierset.name,
        isInactive: !tierset.active,
      };
    }) || [];
  const areTiersetsAdded = tabs.length > 1;

  useEffect(() => {
    if (
      filteredTierSetList &&
      activeTab !== filteredTierSetList[0]?.tierSetId
    ) {
      setActiveTab(filteredTierSetList[0]?.tierSetId);
    }
    // eslint-disable-next-line
  }, [filteredTierSetList]);

  const availableTiers: Tier[] =
    filteredTierSetList?.find((tierset) => tierset.tierSetId === activeTab)
      ?.tiers || [];
  const isAnyOption = availableTiers.length > 0;
  const tiersInTiersets =
    tierSetList?.items
      .map(
        (tierset) =>
          tierset.tiers.map((tier) => ({
            ...tier,
            tierSet: { id: tierset.tierSetId, name: tierset.name },
          })) || [],
      )
      .reduce((a, b) => [...a, ...b], []) || [];

  return (
    <Field name={name}>
      {({ field, form, meta }: FieldProps) => {
        const setValue = (value: string | undefined) => {
          if (onChange) {
            onChange(value);
          }
          if (multiple) {
            const isValueAlreadyAdded =
              field.value &&
              field.value?.findIndex?.(
                (addedValue: string) => addedValue === value,
              ) !== -1;

            if (isValueAlreadyAdded) {
              const valuesToSave =
                Array.isArray(field.value) &&
                [...field.value]?.filter((item) => item !== value);

              form.setFieldValue(field.name, valuesToSave);
            } else {
              // Check missing initialValue
              if (field.value) {
                return form.setFieldValue(field.name, [...field.value, value]);
              }

              form.setFieldValue(field.name, [value]);
            }
          } else {
            form.setFieldValue(field.name, value);
            setIsOpen(false);
          }
        };

        return (
          <>
            <div ref={anchorRef} className={className}>
              <Value
                value={field.value || []}
                isOpen={isOpen}
                label={label}
                setIsOpen={setIsOpen}
                loadedTiers={tiersInTiersets}
                disabled={disabled}
                dataTestId={dataTestId}
                isLoading={!tierSetList}
                placeholder={placeholder}
                multiple={multiple}
                size={size}
                error={meta.error}
              />
            </div>
            <Popper
              open={isOpen}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              transition
              disablePortal
              sx={{
                zIndex: 1302,
              }}
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin:
                      placement === "bottom-start" ? "left top" : "left bottom",
                  }}
                >
                  <Paper
                    sx={{
                      width: anchorRef?.current?.offsetWidth || "100%",
                    }}
                  >
                    <ClickAwayListener
                      onClickAway={(_) => {
                        setSearchedValue("");
                        setIsOpen(false);
                      }}
                    >
                      <Box className={classes.content}>
                        <Search
                          progressClassName={classes.loader}
                          placeholder={`${message({ id: "searchForTiers" })}...`}
                          searchedValue={searchedValue}
                          setSearchedValue={setSearchedValue}
                          isLoading={isTyping}
                        />

                        {areTiersetsAdded && (
                          <div className={classes.tabsWrapper}>
                            <OLTabs
                              items={tabs}
                              className={classes.tabs}
                              value={activeTab || false}
                              onChange={(
                                _: React.SyntheticEvent,
                                newValue: string,
                              ) => {
                                setActiveTab(newValue);
                              }}
                              data-test-id="tabs"
                              maxLabelLength={15}
                            />
                          </div>
                        )}
                        <MenuList className={classes.list} role="listbox">
                          {isAnyOption ? (
                            availableTiers.map(({ name, levelId, active }) => {
                              const isInactive = !active;
                              const isSelected =
                                field.value?.includes &&
                                field.value?.includes(levelId);

                              return (
                                <MenuItem
                                  className={classes.item}
                                  key={levelId}
                                  onClick={() => setValue(levelId)}
                                >
                                  <Checkbox checked={isSelected} />
                                  <span className={classes.tierName}>
                                    {name}
                                  </span>
                                  {isInactive && (
                                    <div className={classes.inactiveTier}>
                                      <span className={classes.dot} />
                                      <span>{message({ id: "inactive" })}</span>
                                    </div>
                                  )}
                                </MenuItem>
                              );
                            })
                          ) : (
                            <MenuItem className={classes.noOption}>
                              {message({ id: "noOptions" })}
                            </MenuItem>
                          )}
                        </MenuList>
                      </Box>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </>
        );
      }}
    </Field>
  );
};
